import { css } from '@emotion/css';

import { TileGroup } from '@/components/diagrams/components/Tile/TileGroup';
import { TileGroupItem } from '@/components/diagrams/components/Tile/TileGroupItem';
import {
  DiagramNodeDisplay,
  DiagramNodeShape,
  GraphVizIconShape,
  GraphVizNodeComponentType,
  GraphVizTileEmphasis,
  ICON_SHAPE_BY_NODE_SHAPE,
  isDiagramHexColor,
  SemanticTileKind,
  type TileFontSizeVariant,
  TileVariant,
} from '@/components/diagrams/diagramStyle';
import { type TileNodeData } from '@/components/diagrams/FlowChart/nodes/TileNode/TileNode';
import { COLORS } from '@/styles/tokens/colors';
import {
  DiagramNodeEstateCategory,
  DiagramNodeKind,
  DiagramNodeSubtype,
} from '@/types/schema';

import { type DiagramBuilderNode } from './diagram';

const DEFAULT_FONT_SIZE: TileFontSizeVariant = 'SM';

/**
 * The default tile color of each estate category. A node without a category
 * uses the default (gray) tile. Colors are tenant theme tokens.
 */
const ESTATE_CATEGORY_VARIANT: Record<DiagramNodeEstateCategory, TileVariant> =
  {
    [DiagramNodeEstateCategory.InEstate]: TileVariant.Primary,
    [DiagramNodeEstateCategory.OutOfEstateFamily]: TileVariant.Secondary,
    [DiagramNodeEstateCategory.OutOfEstateCharity]: TileVariant.Tertiary,
  };

/**
 * Icon tiles of people and documents have a black icon background by default.
 * No shared tile color is black, so this class replaces the icon background
 * of the tile color. `!important` keeps the white icon on hover and
 * selection, where the tile color sets the icon color.
 */
const BLACK_ICON_CLASS = css({
  background: `${COLORS.PRIMITIVES.BLACK} !important`,
  color: `${COLORS.PRIMITIVES.WHITE} !important`,
});

/**
 * The text spacing of a builder tile, as in the Figma component anatomy: the
 * subtitle is 4px below the title, and the notes are 12px below them. The
 * notes field sets its own space. The doubled selector wins over the shared
 * tile line styles.
 */
const TILE_TEXT_CLASSES = {
  lineOne: css({ '&&': { marginBottom: 0 } }),
  lineTwo: css({ '&&': { marginTop: 4 } }),
  lineThree: css({ '&&': { marginTop: 8 } }),
};

/**
 * The layout of a builder icon, as in the Figma component anatomy: 12px of
 * space around the icon and its fields, and a 4px gap between them.
 */
const ICON_INNER_ROOT_CLASS = css({ '&&': { gap: 4, padding: 12 } });

/**
 * The tile props that the node content decides. The React Flow conversion
 * adds the text, the size, and the edit actions.
 */
export type DiagramTile = Pick<
  TileNodeData,
  | 'variant'
  | 'customColor'
  | 'componentType'
  | 'kind'
  | 'iconShape'
  | 'tileEmphasis'
  | 'fontSizeVariant'
  | 'footerLabel'
  | 'children'
  | 'classes'
  | 'lineTwoVariant'
>;

/**
 * Returns the tile for a builder node, as the catalogue in Figma shows it
 * (Artifacts Component Catalogue, A01–A10).
 *
 * A node with a member array is a collection, so this function checks it
 * before the kind:
 *
 * - People collection: an icon tile with the family icon and the item count.
 * - Asset or trust collection: a tile with one row for each member that is
 *   on the diagram. An empty array means that the members are not on the
 *   diagram, not that the collection is empty, so the tile shows no rows.
 * - A collection of any other kind uses the single tile of its kind.
 *
 * A node with the SHAPE display draws as its `nodeStyle.shape`, and the
 * label goes inside the shape. SHAPE nodes have this display by default.
 * A single person or document is an icon tile. Entities, assets, summaries,
 * and payments are tiles. A document icon is square, and people and document
 * icons are black. A payment is destructive. The estate category gives
 * the default color of the other nodes.
 *
 * An explicit `nodeStyle` value replaces the default for that property only.
 * It does not change the estate category of the node. A color is a palette
 * token or a hex color. A hex color replaces only the colors of the default
 * tile color, so the tile keeps its default icon.
 */
export function getDiagramTile(
  node: DiagramBuilderNode,
  nodesByID: ReadonlyMap<string, DiagramBuilderNode>
): DiagramTile {
  const style = node.nodeStyle;
  const color = style?.color;
  const customColor = isDiagramHexColor(color) ? color : undefined;
  const paletteColor = customColor ? undefined : color;
  const variant = (paletteColor ?? getDefaultVariant(node)) as TileVariant;
  const fontSizeVariant = (style?.fontSize ??
    DEFAULT_FONT_SIZE) as TileFontSizeVariant;
  const members = (node.memberNodeIds ?? []).flatMap((id) => {
    const member = nodesByID.get(id);
    return member ? [member] : [];
  });
  const isCollection = node.memberNodeIds != null;
  const isRowCollection =
    isCollection &&
    (node.kind === DiagramNodeKind.Asset ||
      (node.kind === DiagramNodeKind.Entity &&
        node.subtype === DiagramNodeSubtype.Trust));

  // The display changes how a node draws, not its kind.
  const componentType = (style?.display ??
    getDefaultDisplay(node.kind)) as GraphVizNodeComponentType;
  const isShape = componentType === DiagramNodeDisplay.Shape;
  // A people collection shows its item count as its footer. A row collection
  // shows its member rows.
  const getCollectionFooter = () =>
    isCollection && !isRowCollection && members.length > 0
      ? `Items (${members.length})`
      : undefined;
  const hasBlackIcon =
    !style?.color &&
    (node.kind === DiagramNodeKind.Person ||
      node.kind === DiagramNodeKind.Document);

  return {
    variant,
    customColor,
    componentType,
    kind: getDiagramNodeIcon(node),
    iconShape: isShape
      ? (ICON_SHAPE_BY_NODE_SHAPE[style?.shape as DiagramNodeShape] ??
        GraphVizIconShape.Rounded)
      : ((style?.iconShape ??
          (node.kind === DiagramNodeKind.Document
            ? GraphVizIconShape.Square
            : GraphVizIconShape.Circle)) as GraphVizIconShape),
    tileEmphasis: (style?.emphasis ??
      GraphVizTileEmphasis.Low) as GraphVizTileEmphasis,
    fontSizeVariant,
    // A builder tile shows its subtitle in the style of its details, not as
    // the large value line of the catalogue tiles.
    lineTwoVariant: 'body-xs',
    classes: {
      ...TILE_TEXT_CLASSES,
      ...(componentType === DiagramNodeDisplay.TileIcon && {
        innerRoot: ICON_INNER_ROOT_CLASS,
      }),
      ...(hasBlackIcon && { icon: BLACK_ICON_CLASS }),
    },
    // A tile setting can hide the footer, also the item count.
    footerLabel: style?.hideFooterLabel
      ? undefined
      : node.footerLabel || getCollectionFooter(),
    children:
      isRowCollection && members.length > 0 ? (
        <TileGroup
          tileVariant={variant}
          tileCustomColor={customColor}
          fontSizeVariant={fontSizeVariant}
        >
          {members.map((member) => (
            <TileGroupItem
              key={member.nodeID}
              description={member.label}
              value={member.subtitle ?? undefined}
              tileVariant={variant}
              tileCustomColor={customColor}
              fontSizeVariant={fontSizeVariant}
            />
          ))}
        </TileGroup>
      ) : undefined,
  };
}

/** Returns the tile color of a node without a color override. */
function getDefaultVariant(node: DiagramBuilderNode): TileVariant {
  if (node.kind === DiagramNodeKind.Payment) return TileVariant.Destructive;
  if (node.estateCategory) return ESTATE_CATEGORY_VARIANT[node.estateCategory];
  return TileVariant.Default;
}

/** People and documents are icon tiles in the catalogue. */
function getDefaultDisplay(kind: DiagramNodeKind) {
  switch (kind) {
    case DiagramNodeKind.Person:
    case DiagramNodeKind.Document:
      return DiagramNodeDisplay.TileIcon;
    case DiagramNodeKind.Entity:
    case DiagramNodeKind.Asset:
    case DiagramNodeKind.Summary:
    case DiagramNodeKind.Payment:
    // The builder draws TEXT nodes with their own node component.
    case DiagramNodeKind.Text:
      return DiagramNodeDisplay.Tile;
    case DiagramNodeKind.Shape:
      return DiagramNodeDisplay.Shape;
  }
}

/**
 * Returns the catalogue icon of a kind, or undefined to use the shared icon
 * of the tile color. Only icon tiles show an icon.
 */
/**
 * Returns the icon of a node: its icon override, or the catalogue icon of
 * its kind. A people collection has the People icon. Undefined means that
 * the tile color selects the icon.
 */
export function getDiagramNodeIcon(
  node: DiagramBuilderNode
): SemanticTileKind | undefined {
  if (node.nodeStyle?.icon) return node.nodeStyle.icon as SemanticTileKind;
  switch (node.kind) {
    case DiagramNodeKind.Person:
      return node.memberNodeIds != null
        ? SemanticTileKind.Family
        : SemanticTileKind.Person;
    case DiagramNodeKind.Document:
      return SemanticTileKind.Document;
    case DiagramNodeKind.Entity:
    case DiagramNodeKind.Asset:
    case DiagramNodeKind.Summary:
    case DiagramNodeKind.Payment:
    case DiagramNodeKind.Shape:
    case DiagramNodeKind.Text:
      return undefined;
  }
}
